/**
 * Widget Actualités — bandeau défilant (« ticker ») façon fil d'info.
 * Une étiquette fixe à gauche, puis les titres qui défilent en continu.
 * La vitesse, la catégorie et l'affichage source/date sont configurables.
 * Données factices pour l'instant (voir `api/news.ts`).
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext } from "react";
import { NewsCategory, NewsItem } from "../../../../api/news";
import { useNews } from "./useNews";
import { WidgetPreviewContext, WidgetShell } from "./WidgetShell";

export type NewsWidgetProps = {
  /** Catégorie filtrée ("all" = toutes). */
  category?: NewsCategory | "all";
  /** Vitesse de défilement du bandeau. */
  speed?: "slow" | "normal" | "fast";
  showSource?: boolean;
  showDate?: boolean;
};

/** Durée d'un cycle de défilement (s) selon la vitesse. Plus long = plus lent. */
const SPEED_DURATION: Record<NonNullable<NewsWidgetProps["speed"]>, number> = {
  slow: 60,
  normal: 40,
  fast: 25,
};

/** Date relative compacte (« il y a 2 h », « il y a 3 j »). */
function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `il y a ${Math.max(1, minutes)} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  return `il y a ${days} j`;
}

export const NewsWidget = ({
  category = "all",
  speed = "normal",
  showSource = true,
  showDate = true,
}: NewsWidgetProps) => {
  const isPreview = useContext(WidgetPreviewContext);
  const { items, loading, error } = useNews(category);

  return (
    <WidgetShell removable>
      <div className="h-full flex items-center gap-3 min-w-0">
        <div className="shrink-0 flex items-center gap-1.5 rounded-md bg-tertiary px-2 py-1 text-secondary-text">
          <FontAwesomeIcon icon="newspaper" className="text-sm" />
          <span className="text-xs font-oxaniumMedium uppercase tracking-wide">
            Actu
          </span>
        </div>

        <div className="relative flex-1 min-w-0 h-full overflow-hidden flex items-center">
          {error ? (
            <p className="text-sm text-grey-medium">
              Actualités indisponibles pour le moment.
            </p>
          ) : loading && items.length === 0 ? (
            <div className="h-3 w-2/3 rounded bg-grey-light animate-pulse" />
          ) : items.length === 0 ? (
            <p className="text-sm text-grey-medium">Aucune actualité.</p>
          ) : (
            <Ticker
              items={items}
              duration={SPEED_DURATION[speed]}
              showSource={showSource}
              showDate={showDate}
              // En aperçu (catalogue / configurateur), on fige le défilement pour
              // un rendu lisible et stable.
              paused={isPreview}
            />
          )}
        </div>
      </div>
    </WidgetShell>
  );
};

function Ticker({
  items,
  duration,
  showSource,
  showDate,
  paused,
}: {
  items: NewsItem[];
  duration: number;
  showSource: boolean;
  showDate: boolean;
  paused: boolean;
}) {
  return (
    <div
      className={`flex w-max shrink-0 items-center whitespace-nowrap will-change-transform animate-news-ticker hover:[animation-play-state:paused] ${
        paused ? "[animation-play-state:paused]" : ""
      }`}
      style={{ animationDuration: `${duration}s` }}
    >
      {/* Deux copies consécutives → la boucle (-50%) est sans couture. */}
      {[0, 1].map((copy) => (
        <div key={copy} className="flex items-center" aria-hidden={copy === 1}>
          {items.map((item) => (
            <TickerEntry
              key={`${copy}-${item.id}`}
              item={item}
              showSource={showSource}
              showDate={showDate}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function TickerEntry({
  item,
  showSource,
  showDate,
}: {
  item: NewsItem;
  showSource: boolean;
  showDate: boolean;
}) {
  const meta = [
    showSource ? item.source : null,
    showDate ? formatRelative(item.publishedAt) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <span className="flex items-center text-sm text-default-text">
      <span className="mx-3 text-secondary" aria-hidden>
        •
      </span>
      <span className="font-oxaniumMedium">{item.title}</span>
      {meta && <span className="ml-2 text-xs text-grey-medium">{meta}</span>}
    </span>
  );
}
